import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { PopupProvider, PopupRecord } from "./PopupProvider";
import { styled, Theme, Button, Box, useTheme, useMediaQuery, IconButton } from "@mui/material";
import { MobileRoute } from "../MobileRoute";
import { FiMaximize2, FiMinimize2, } from "react-icons/fi";
import { IoClose, IoRemoveOutline } from "react-icons/io5";
import { TextOverflow } from "../TextOverflow";

export type PopupModel = {
    id: string;
    name: string;
    body: React.ReactNode;
    icon: React.ReactNode,
    title: string;
    initialWidth?: number;
    initialHeight?: number;
    hideFooter?: boolean;
    removeFromDomOnMinimize?: boolean;
}

export const PopupWrapper = styled('div', {})(({ theme }: { theme: Theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    boxSizing: "border-box",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: theme.zIndex.drawer * 2,
    backgroundColor: theme.palette.background.default,
    resize: "both",
    overflow: "auto",
    minWidth: "300px",
    minHeight: "300px",
    borderRadius: theme.shape.borderRadius,
}));

export const PopupHeader = styled("header", {})(({ theme }) => ({
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "grab",
    backgroundColor: theme.palette.grey[50],
}));

export const PopupBody = styled("main", {})(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexGrow: "1",
    width: "100%",
    overflow: "auto",
    position: "relative",
}));

export const PopupFooter = styled("footer", {})(({ theme }) => ({
    gap: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    position: "relative",
    backgroundColor: theme.palette.grey[50],
    width: "100%",
}));

export const Popup = ({ popup: popupFromProvider }: { popup: PopupRecord }) => {
    const theme = useTheme();

    const [popup, setLocalPopup] = useState(popupFromProvider);

    const { closePopup, focusPopup, hidePopup, updatePopup: updatePopupInProvider } = PopupProvider.usePopups();

    const [isDragging, setIsDragging] = useState(false);

    const [offset, setOffset] = useState<{ x: number, y: number }>({ x: 0, y: 0 });

    const popupRef = useRef<HTMLDivElement>(null);

    const onlyMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

    const updatePopup = (data: { id: string; } & Partial<PopupRecord>) => {
        updatePopupInProvider(data, false);
        setLocalPopup((prev) => ({ ...prev, ...data }));
    }

    const handleMaximized = (isMaximized: boolean) => {
        updatePopup({ id: popup.id, isMaximized });
    }

    // Event handler for starting drag
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        focusPopup(popup.id);
        setIsDragging(true);
        setOffset({
            x: e.clientX - popup.x,
            y: e.clientY - popup.y
        });
    };

    // Event handler for ending drag
    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Event handler for moving popup
    const handleMouseMove = (e: MouseEvent) => {
        updatePopup({ id: popup.id, isMaximized: false, isPinnedLeft: false, isPinnedRight: false });
        if (!popupRef.current) return;
        if (!isDragging) return;
        const y = e.clientY - offset.y;
        const x = e.clientX - offset.x;

        const getPinnedPosition = (): Partial<PopupRecord> => {
            if (y < -20) return { isMaximized: true };
            if (x < -20) return { isPinnedLeft: true };
            if (e.clientX > window.innerWidth + 20) return { isPinnedRight: true };
            return {};
        }

        updatePopup({
            id: popup.id,
            x,
            y,
            ...getPinnedPosition(),
        });
    };

    // Adding user-select: none to body when dragging to prevent text selection
    const toggleSelection = (enabled: boolean) => {
        document.body.style.userSelect = enabled ? 'auto' : 'none';
    }

    // Attach mouse move and mouse up event listeners to window
    useEffect(() => {
        if (isDragging) {
            toggleSelection(false);
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                toggleSelection(true);
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging]);

    useLayoutEffect(() => {
        if (popupRef.current && popup.x == 0 && popup.y == 0) {
            updatePopup({
                id: popup.id,
                x: window.innerWidth / 2 - popupRef.current.offsetWidth / 2,
                y: window.innerHeight / 2 - popupRef.current.offsetHeight / 2
            });
        }

        if (popupRef.current) {
            let isInitial = true;

            const popupElement = popupRef.current;

            const { lastWidth, lastHeight } = popup;

            if (lastWidth && lastHeight) {
                popupElement.style.width = `${lastWidth}px`;
                popupElement.style.height = `${lastHeight}px`;
            }

            const resizeObserver = new ResizeObserver((_entries) => {
                if (isInitial) {
                    isInitial = false;
                    return;
                }
                updatePopup({
                    id: popup.id,
                    lastWidth: popupElement.offsetWidth,
                    lastHeight: popupElement.offsetHeight
                });
            });

            resizeObserver.observe(popupElement);

            return () => {
                resizeObserver.unobserve(popupElement);
            };
        }
    }, []);

    const getStyle = (): React.CSSProperties => {
        if (popupFromProvider.hidden) return {
            visibility: "hidden",
            pointerEvents: "none",
        }

        const style: React.CSSProperties = {
            top: popup.y,
            left: popup.x,
            position: 'fixed',
            width: popup.initialWidth || "720px",
            height: popup.initialHeight || "540px",
        };

        if (!popup.isMaximized && !popup.isPinnedLeft && !popup.isPinnedRight) return style;

        const pinnedStyles: React.CSSProperties = {
            top: 0,
            height: "100vh",
            position: "fixed",
            borderRadius: 'none!important',
        }

        if (popup.isPinnedLeft) return {
            left: 0,
            width: "50vw",
            ...pinnedStyles,
        }

        if (popup.isPinnedRight) return {
            right: 0,
            width: "50vw",
            left: "unset",
            ...pinnedStyles,
        }

        return {
            left: 0,
            width: "100vw",
            ...pinnedStyles,
        };
    }

    if (onlyMediumScreen) {
        return <MobileRoute title={popup.title} onClose={() => closePopup(popup.id)}>
            {popup.body}
        </MobileRoute>
    }

    const iconButtonPadding = { padding: "3px" };

    return (
        <PopupWrapper
            ref={popupRef}
            key={popup.id}
            onClick={() => focusPopup(popup.id)}
            style={getStyle()}
        >
            <PopupHeader onMouseDown={handleMouseDown} onDoubleClick={() => handleMaximized(!popup.isMaximized)}>
                <Box display="flex" alignItems="center" overflow="hidden" flexGrow={1} width="100%" gap={1}>
                    <Box display="flex" alignItems="center" fontSize={18}>{popup.icon}</Box>

                    <Box flexGrow={1} width="100%" overflow="hidden">
                        <TextOverflow text={popup.title} variant="h6" lineHeight="24px" />
                    </Box>
                </Box>

                <Box display="flex" alignItems="center" flexShrink={0}>
                    <IconButton sx={iconButtonPadding} onClick={(e) => {
                        e.stopPropagation();
                        hidePopup(popup.id);
                    }} color="warning">
                        <IoRemoveOutline size={24} />
                    </IconButton>

                    <IconButton sx={iconButtonPadding} onClick={(e) => {
                        e.stopPropagation();
                        handleMaximized(!popup.isMaximized);
                    }} color="success">
                        {popup.isMaximized ? <FiMinimize2 size={18} /> : <FiMaximize2 size={18} />}
                    </IconButton>

                    <IconButton sx={iconButtonPadding} onClick={(e) => {
                        e.stopPropagation();
                        closePopup(popup.id);
                    }} color="error">
                        <IoClose size={24} />
                    </IconButton>
                </Box>
            </PopupHeader>

            {!popup.hideFooter && (
                <>
                    <PopupBody>
                        {popup.body}
                    </PopupBody>

                    {!popup.hideFooter && (
                        <PopupFooter>
                            <Button size="small" color="secondary" onClick={(e) => {
                                e.stopPropagation();
                                closePopup(popup.id);
                            }}>
                                Zatvoriť
                            </Button>
                        </PopupFooter>
                    )}
                </>
            )}

            {popup.hideFooter && popup.body}
        </PopupWrapper>
    );
};